import { useEffect, useState } from "react";
import styles from "./HomePage.module.scss";
import Presentation from "./components/Presentation";
import Links from "../../components/Links/Links";
import { fetchRates } from "../../api/rates";
import Cards from "../../components/Cards/Cards";
import HomeHero from "./components/HomeHero/HomeHero";

import { useActivePromotions } from "../../hooks/useActivePromotions";
import PromotionModal from "../../components/Prestations/PromotionModal/PromotionModal";

import Testimonials from "../../components/Testimonials/Testimonials";
import TestimonialForm from "../../components/Testimonials/TestimonialForm";
import Modal from "../../components/Modal/Modal";
import { fetchTestimonials } from "../../api/testimonial";

const getTodayKey = () => {
  const now = new Date();

  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("-");
};

function Home() {
  const [rates, setRates] = useState([]);
  const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);

  const [testimonials, setTestimonials] = useState([]);
  const [errorTestimonials, setErrorTestimonials] = useState(null);
  const [isTestimonialModalOpen, setIsTestimonialModalOpen] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const {
    promotions: activePromotions,
    loading: promotionsLoading,
    error: promotionsError,
  } = useActivePromotions();

  const activePromotion = activePromotions?.[0];

  useEffect(() => {
    const getRates = async () => {
      const response = await fetchRates();
      setRates(response.data);
    };

    getRates();
  }, []);

  useEffect(() => {
    const getTestimonials = async () => {
      try {
        const response = await fetchTestimonials();
        if (response.status === 200) {
          setTestimonials(response.data);
          setErrorTestimonials(null);
        } else {
          setErrorTestimonials(
            "Une erreur s'est produite lors de la récupération des témoignages",
          );
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des témoignages : ",
          error,
        );
        setErrorTestimonials(
          "Une erreur s'est produite lors de la récupération des témoignages",
        );
      }
    };

    getTestimonials();
  }, []);

  useEffect(() => {
    if (!activePromotion) {
      setIsPromotionModalOpen(false);
      return;
    }

    const storageKey = `promotion-modal-last-display-${activePromotion.id}`;
    const today = getTodayKey();

    try {
      const lastDisplayDate = localStorage.getItem(storageKey);

      setIsPromotionModalOpen(lastDisplayDate !== today);
    } catch {
      setIsPromotionModalOpen(true);
    }
  }, [activePromotion]);

  const closePromotionModal = () => {
    if (activePromotion) {
      const storageKey = `promotion-modal-last-display-${activePromotion.id}`;

      try {
        localStorage.setItem(storageKey, getTodayKey());
      } catch {
        // On ferme tout de même la modal si le stockage est inaccessible.
      }
    }

    setIsPromotionModalOpen(false);
  };

  const openTestimonialModal = () => {
    setIsTestimonialModalOpen(true);
  };

  const closeTestimonialModal = () => {
    setIsTestimonialModalOpen(false);
  };

  const handleTestimonialSubmitSuccess = () => {
    setShowConfirmationModal(true);
    closeTestimonialModal();
  };

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
    // Optionnel : recharger les témoignages ici
  };

  return (
    <>
      <div className={styles.container}>
        <HomeHero />

        <Presentation />

        <Cards title="prestations" data={rates} />

        <Testimonials
          testimonials={testimonials}
          onOpenModal={openTestimonialModal}
        />

        <Links />
      </div>

      {!promotionsLoading &&
        !promotionsError &&
        activePromotion &&
        isPromotionModalOpen && (
          <PromotionModal
            promotion={activePromotion}
            onClose={closePromotionModal}
          />
        )}

      {/* Modal formulaire de témoignage */}
      <Modal isOpen={isTestimonialModalOpen} onClose={closeTestimonialModal}>
        <TestimonialForm
          onClose={closeTestimonialModal}
          onSubmitSuccess={handleTestimonialSubmitSuccess}
        />
      </Modal>

      {/* Modal confirmation d’envoi */}
      <Modal isOpen={showConfirmationModal} onClose={closeConfirmationModal}>
        <div style={{ textAlign: "center" }}>
          <p>Votre témoignage a été envoyé avec succès !</p>
        </div>
      </Modal>

      {/* Modal d’erreur témoignages (optionnel) */}
      {errorTestimonials && (
        <Modal
          isOpen={!!errorTestimonials}
          onClose={() => setErrorTestimonials(null)}
        >
          <div style={{ textAlign: "center" }}>
            <p>{errorTestimonials}</p>
          </div>
        </Modal>
      )}
    </>
  );
}

export default Home;
